import React from 'react';
import { Link } from 'react-router-dom';
import { mentionPath } from '../../lib/mentions';
import { parseFormatted, formattedPreview } from '../../lib/formatText';

// Posts and replies: bold, italic, lists, bare-URL links and @mentions. See
// src/lib/formatText.js for the supported subset and why it is a subset.
//
// Video comments stay on LinkifiedText — they are plain on purpose.
//
// Same wrapping and link treatment as LinkifiedText: `[overflow-wrap:anywhere]`
// keeps long URLs inside the card, and stopPropagation keeps a tap on a link
// inside a clickable card from also opening the post.

const renderInline = (nodes) => nodes.map((node, i) => {
  switch (node.type) {
    case 'strong':
      return <strong key={i} className="font-semibold text-primary">{renderInline(node.children)}</strong>;
    case 'em':
      return <em key={i}>{renderInline(node.children)}</em>;
    case 'link':
      return (
        <a
          key={i}
          href={node.href}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
          onClick={(e) => e.stopPropagation()}
          className="underline underline-offset-2 hover:opacity-80"
          style={{ color: 'var(--brand-primary)' }}
        >
          {node.value}
        </a>
      );
    case 'mention':
      // In-app route (/u/:username resolves to the profile), so a router
      // Link rather than a new tab.
      return (
        <Link
          key={i}
          to={mentionPath(node.handle)}
          onClick={(e) => e.stopPropagation()}
          className="font-medium hover:underline underline-offset-2"
          style={{ color: 'var(--brand-primary)' }}
        >
          {node.value}
        </Link>
      );
    default:
      return <React.Fragment key={i}>{node.value}</React.Fragment>;
  }
});

const FormattedText = ({ text, className = '' }) => {
  const blocks = parseFormatted(text);
  return (
    <div className={`[overflow-wrap:anywhere] space-y-2 ${className}`}>
      {blocks.map((block, i) => {
        if (block.type === 'p') {
          return <p key={i} className="whitespace-pre-wrap">{renderInline(block.children)}</p>;
        }
        const ListTag = block.type === 'ol' ? 'ol' : 'ul';
        return (
          <ListTag
            key={i}
            start={block.type === 'ol' ? block.start : undefined}
            className={`pl-6 space-y-1 ${block.type === 'ol' ? 'list-decimal' : 'list-disc'}`}
          >
            {block.items.map((item, j) => (
              <li key={j} className="pl-1">{renderInline(item.children)}</li>
            ))}
          </ListTag>
        );
      })}
    </div>
  );
};

// A clamped or cut preview (table list, feed before "Read more"): bold, italic
// and links kept, lists flattened to "• item" lines in one block so
// line-clamp works. Pass `maxChars` to cut; the caller can ask
// formattedPreview(text, max).truncated to decide whether to offer "Read more".
export const FormattedPreview = ({ text, maxChars, trailing = null, className = '' }) => {
  const { nodes } = formattedPreview(text, maxChars);
  return (
    <p className={`whitespace-pre-wrap [overflow-wrap:anywhere] ${className}`}>
      {renderInline(nodes)}
      {trailing}
    </p>
  );
};

export default FormattedText;
